import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { segmentTypesList } from './api';
import { createApiOnSubmit } from './forms';
import { FlyoutManager } from './flyoutManager';
import { pushEvent } from './analytics';

export function ProjectBoardForm({ board, onBoardSaved, onSubmitApi, flyoutId }) {
    const deriveSegmentIds = React.useCallback((sourceBoard) => {
        if (!sourceBoard) return [];

        if (Array.isArray(sourceBoard.segmentTypeIds) && sourceBoard.segmentTypeIds.length) {
            return sourceBoard.segmentTypeIds
                .map(s => {
                    if (typeof s === 'number') return s;
                    if (typeof s === 'string') return parseInt(s, 10);
                    return s?.id;
                })
                .filter(value => Number.isFinite(value))
                .map(id => id.toString());
        }

        if (Array.isArray(sourceBoard.segmentTypes)) {
            return sourceBoard.segmentTypes.map(s => s.id.toString());
        }

        return [];
    }, []);

    const boardSegmentIds = React.useMemo(() => deriveSegmentIds(board), [board, deriveSegmentIds]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitSuccessful },
        setError,
        reset
    } = useForm({
        reValidateMode: 'onSubmit',
        defaultValues: {
            name: board?.name || '',
            segmentTypeIds: boardSegmentIds,
            notes: board?.notes || ''
        }
    });

    const [segmentTypes, setSegmentTypes] = useState([]);
    const [boardId, setBoardId] = useState(board?.id || null);

    const boardName = watch("name");
    const selectedSegments = watch("segmentTypeIds");

    useEffect(() => {
        if (!board) return;
        const nextValues = {
            name: board.name || '',
            segmentTypeIds: boardSegmentIds,
            notes: board.notes || ''
        };
        reset(nextValues);
        setBoardId(board.id || null);
    }, [board, boardSegmentIds, reset]);

    // Load segments
    useEffect(() => {
        segmentTypesList()
            .then(data => {
                setSegmentTypes(data);
            })
            .catch(e => console.error('Error loading segment types:', e));
    }, []);

    // Handle flyout close after successful submit
    useEffect(() => {
        if (isSubmitSuccessful && boardId) {
            if (flyoutId) {
                FlyoutManager.close(flyoutId);
            }
            reset();
            if (onBoardSaved) onBoardSaved(boardId);
        }
    }, [isSubmitSuccessful, boardId, flyoutId, reset, onBoardSaved]);

    const onSubmit = createApiOnSubmit(async data => {
        let id;

        const segmentTypeIds = Array.isArray(data.segmentTypeIds)
            ? data.segmentTypeIds.map(id => parseInt(id, 10)).filter(Number.isFinite)
            : [];

        if (onSubmitApi) {
            id = await onSubmitApi({
                id: board?.id,
                name: data.name,
                segmentTypeIds,
                notes: data.notes
            });
        } else {
            const { projectBoardsCreate } = await import('./api');
            id = await projectBoardsCreate({
                name: data.name,
                segmentTypeIds,
                notes: data.notes
            });
        }

        setBoardId(id);

        // GTM: Track successful project board creation
        pushEvent({
            'event': 'formSubmission',
            'formID': 'createProjectBoard'
        });

        if (onBoardSaved) {
            onBoardSaved(id, {
                name: data.name,
                notes: data.notes,
                segmentTypeIds
            });
        }

        if (flyoutId) {
            FlyoutManager.close(flyoutId);
        }
    }, setError);


    const isDisabled = !boardName?.trim() || !selectedSegments?.length;

    return (
        <div className='projectboard-flyout'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h4>{board ? 'Edit Project Board' : 'Create a New Project Board'}</h4>

                <div className='projectboard-flyout__group'>
                    <label htmlFor="boardName">Board name</label>
                    <input
                        className='projectboard-flyout__input'
                        id="boardName"
                        type="text"
                        {...register("name", { required: true })}
                    />
                    {errors.root?.Name && <div className='error'>{errors.root.Name.message}</div>}
                </div>

                <div className="projectboard-flyout__group">
                    <p className='label projectboard-flyout__label'>Board segment</p>
                    <div className="projectboard-flyout__segments">
                        {segmentTypes.map(segmentType => (
                            <label key={segmentType.id} className="segment-option">
                                <input
                                    type="checkbox"
                                    value={segmentType.id}
                                    {...register("segmentTypeIds")}
                                    defaultChecked={boardSegmentIds.includes(segmentType.id.toString())}
                                />
                                <span>{segmentType.name}</span>
                            </label>
                        ))}
                    </div>
                    {errors.root?.SegmentTypeIds && (
                        <div className="error">{errors.root.SegmentTypeIds.message}</div>
                    )}
                </div>

                <div className='projectboard-flyout__group'>
                    <label htmlFor="boardNotes">Board notes</label>
                    <textarea
                        id="boardNotes"
                        {...register("notes")}
                        rows={6}
                    />
                    {errors.root?.Notes && <div className='error'>{errors.root.Notes.message}</div>}
                </div>

                <div>
                    <button className='btn' type="submit" disabled={isDisabled}>
                        {board ? 'Update Board' : 'Create Board'}
                    </button>
                </div>
            </form>
        </div>
    );
}
